import { useEffect, useId, useState } from "react";
import { Field, useFormState } from "react-final-form";
import { useText } from "../../../hooks";
import type { QuestionnaireFormValues } from "../../../types/form";
import type { LangKey } from "../../../types/lang";
import type { AnswerChoice, SimpleAnswer, SimpleQuestion } from "../../../types/question";
import { isRequired } from "../../../utils/validation";
import { Fieldset, FormControlLabel, FormHelperText, Legend } from "../../form-fields";
import { RadioButton } from "../../form-fields/components/radio";
import { Stack } from "../../stack";
import { formatFieldName } from "../utils";

type Props = {
	question: SimpleQuestion;
};

const isFieldChecked = (
	values: QuestionnaireFormValues<SimpleAnswer>,
	choice: AnswerChoice<string>,
	questionId: string,
) => {
	const field = values[formatFieldName(questionId)];
	if (!field) return false;
	return field.some((v) => v.answerChoiceId === choice.answerChoiceId);
};

const formatAnswer = (choice: AnswerChoice<string>): SimpleAnswer[] => [
	{ answerChoiceId: choice.answerChoiceId, answerValue: choice.value },
];

const SingleChoiceQuestion = ({ question }: Props) => {
	const { questionId, question: questionText, answerChoices } = question;
	const describedById = useId();
	const [hasSubmitFailed, setHasSubmitError] = useState(false);
	const { values: formValues } = useFormState<QuestionnaireFormValues<SimpleAnswer>>();
	const [t] = useText();
	return (
		<Fieldset
			aria-invalid={hasSubmitFailed}
			aria-describedby={hasSubmitFailed ? describedById : undefined}
		>
			<Legend>{questionText}</Legend>
			<Stack gap={2}>
				<Field<Array<SimpleAnswer>>
					name={formatFieldName(questionId)}
					type="radio"
					parse={(v) => v || []}
					format={(v) => v || []} // defaults the field to empty array instead of empty string
					validate={(values = []) => isRequired(values.map((v) => v.answerValue))} // todo: custom validation fn?
				>
					{({ input, meta }) => {
						// biome-ignore lint/correctness/useHookAtTopLevel: Not conditional
						useEffect(() => {
							setHasSubmitError(!!meta.submitFailed && !!meta.error);
						}, [meta]);
						return (
							<>
								{answerChoices.map((answerChoice) => (
									<FormControlLabel
										data-testid={answerChoice.answerChoiceId}
										label={answerChoice.value}
										key={answerChoice.answerChoiceId}
										gap={2}
									>
										<RadioButton
											name={input.name}
											value={answerChoice.value}
											onChange={() => {
												input.onChange(formatAnswer(answerChoice));
											}}
											checked={isFieldChecked(formValues, answerChoice, questionId)}
										/>
									</FormControlLabel>
								))}
								{meta.submitFailed && (
									<FormHelperText error={!!meta.error && meta.submitFailed} id={describedById}>
										{t(meta.error as LangKey)}
									</FormHelperText>
								)}
							</>
						);
					}}
				</Field>
			</Stack>
		</Fieldset>
	);
};

export default SingleChoiceQuestion;
